import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
} from "@mui/material";
import { WarningAmberRounded, DeleteOutlineRounded } from "@mui/icons-material";
import TlButton from "./TlButton";

/**
 * ConfirmDialog — destructive action confirmation
 *
 * open         — boolean
 * onClose      — () => void
 * onConfirm    — () => void  (async-safe: button is disabled while loading)
 * title        — dialog heading
 * description  — supporting explanation (what exactly will be deleted/changed)
 * confirmLabel — text on confirm button (default "Delete")
 * cancelLabel  — text on cancel button (default "Cancel")
 * severity     — "danger" | "warning" (default "danger")
 * loading      — disables buttons and shows spinner on confirm
 *
 * Accessibility: focus trapped inside (MUI default); cancel receives initial focus.
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Delete",
  cancelLabel  = "Cancel",
  severity     = "danger",
  loading      = false,
}) {
  const isDanger  = severity === "danger";
  const accentColor = isDanger ? "var(--danger)"  : "var(--warning)";
  const iconBg      = isDanger ? "var(--danger-bg)" : "var(--warning-bg)";
  const Icon        = isDanger ? DeleteOutlineRounded : WarningAmberRounded;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "var(--r-xl)",
          overflow: "hidden",
          fontFamily: "var(--font-ui)",
        },
      }}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      {/* Severity accent strip */}
      <Box sx={{ height: 3, background: accentColor }} />

      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          pt: 3, pb: 1.5, px: 3,
          display: "flex", alignItems: "flex-start", gap: 2,
        }}
      >
        <Box sx={{
          flexShrink: 0,
          width: 44, height: 44,
          borderRadius: "var(--r-md)",
          background: iconBg,
          color: accentColor,
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { fontSize: 24 },
        }}>
          <Icon />
        </Box>

        <Box sx={{ pt: 0.5 }}>
          <Typography component="span" sx={{
            display: "block",
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "var(--t-h4)",
            color: "var(--ink-900)",
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
          }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      {description && (
        <DialogContent sx={{ pt: 0, px: 3, pb: 2 }}>
          <Typography
            id="confirm-dialog-description"
            sx={{
              fontSize: "var(--t-body-sm)",
              color: "var(--ink-500)",
              lineHeight: 1.65,
              pl: "60px",
            }}
          >
            {description}
          </Typography>
        </DialogContent>
      )}

      <DialogActions sx={{ px: 3, pb: 3, pt: description ? 0.5 : 1, gap: 1, justifyContent: "flex-end" }}>
        <TlButton
          variant="secondary"
          size="md"
          onClick={onClose}
          disabled={loading}
          autoFocus
        >
          {cancelLabel}
        </TlButton>

        <TlButton
          variant={isDanger ? "danger" : "primary"}
          size="md"
          onClick={onConfirm}
          disabled={loading}
          loading={loading}
        >
          {confirmLabel}
        </TlButton>
      </DialogActions>
    </Dialog>
  );
}
